import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, type AnimationDefinition, motion } from 'framer-motion';
import { RoomAudioRenderer, StartAudio, useSessionContext } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { GuestConnectionProvider, useGuestConnection } from '@/hooks/useGuestConnection';
import { useTranscriptCapture } from '@/hooks/useTranscriptCapture';
import { getAppConfig } from '@/lib/utils';
import { GuestWelcomeView } from '@/components/app/guest-welcome-view';
import { GuestSessionView } from '@/components/app/guest-session-view';
import { Toaster } from '@/components/livekit/toaster';
import { LanguageSwitcher } from '@/components/app/LanguageSwitcher';
import { useLangfuseSession } from '@/hooks/useLangfuseSession';
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';

const MotionWelcomeView = motion.create(GuestWelcomeView);
const MotionSessionView = motion.create(GuestSessionView);

const VIEW_MOTION_PROPS = {
  variants: {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  },
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  transition: { duration: 0.5, ease: 'linear' },
};

type GuestPageState = 'loading' | 'ready' | 'interview' | 'saving' | 'completed' | 'error';

interface GuestInterviewContentProps {
  participantId: string;
  designId: string;
  appConfig: ReturnType<typeof getAppConfig>;
}

function GuestInterviewContent({ participantId, designId, appConfig }: GuestInterviewContentProps) {
  const { t } = useTranslation();
  const {
    isConnectionActive,
    connect,
    onDisconnectTransitionComplete,
    connectionError,
    participantName,
  } = useGuestConnection();
  const { transcript, getDuration } = useTranscriptCapture();
  const [pageState, setPageState] = useState<GuestPageState>('ready');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Get session and room for Langfuse session tracking
  const session = useSessionContext();
  const langfuseSessionId = useLangfuseSession(session?.room);

  // DEBUG: Log session tracking
  useEffect(() => {
    console.log('🔍 [DEBUG] Session tracking:', {
      hasSession: !!session,
      hasRoom: !!session?.room,
      langfuseSessionId,
      pageState
    });
  }, [session, langfuseSessionId, pageState]);


  // Feedback dialog state
  const [showFeedback, setShowFeedback] = useState(false);

  // Handle connection errors
  useEffect(() => {
    if (connectionError) {
      console.log('❌ [DEBUG] Connection error, setting state to error');
      setPageState('error');
    }
  }, [connectionError]);

  // DEBUG: Log page state changes
  useEffect(() => {
    console.log('📊 [DEBUG] Page state changed:', pageState);
  }, [pageState]);

  // Save transcript when session ends
  const saveTranscript = useCallback(async () => {
    if (pageState === 'saving' || pageState === 'completed') return;

    setPageState('saving');

    try {
      const duration = getDuration();

      console.log('[GuestInterview] Saving transcript:', {
        participantId,
        designId,
        transcriptLength: transcript.length,
        duration,
      });

      const response = await fetch('/api/guest-results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          participantId,
          designId,
          transcript,
          duration,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('[GuestInterview] Transcript saved:', result);

      setPageState('completed');
    } catch (error) {
      console.error('[GuestInterview] Error saving transcript:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save interview');
      setPageState('completed'); // Still show completion even if save failed
    }
  }, [participantId, designId, transcript, getDuration, pageState]);

  const handleAnimationComplete = useCallback(
    (definition: AnimationDefinition) => {
      if (definition === 'hidden') {
        onDisconnectTransitionComplete();
        // Save transcript when session ends
        if (pageState === 'interview') {
          saveTranscript();
        }
      }
    },
    [onDisconnectTransitionComplete, pageState, saveTranscript]
  );

  const handleStartCall = useCallback(() => {
    setPageState('interview');
    connect();
  }, [connect]);

  // Render based on state
  if (pageState === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="mx-4 max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('guest.connectionError')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {connectionError || t('guest.unknownError')}
          </p>
        </div>
      </div>
    );
  }

  if (pageState === 'saving') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="mx-4 max-w-md text-center">
          <Loader2 className="mx-auto mb-4 h-16 w-16 animate-spin text-blue-500" />
          <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('guest.savingInterview')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('guest.pleaseWait')}
          </p>
        </div>
      </div>
    );
  }

  // Show feedback dialog when completed
  useEffect(() => {
    console.log('🎯 [DEBUG] Feedback dialog check:', {
      pageState,
      langfuseSessionId,
      showFeedback,
      condition: pageState === 'completed' && langfuseSessionId && !showFeedback
    });

    if (pageState === 'completed' && langfuseSessionId && !showFeedback) {
      console.log('⏰ [DEBUG] Setting feedback timeout (500ms)...');
      const timer = setTimeout(() => {
        console.log('✅ [DEBUG] Timeout fired - showing feedback dialog!');
        setShowFeedback(true);
      }, 500);
      return () => {
        console.log('🧹 [DEBUG] Cleaning up feedback timeout');
        clearTimeout(timer);
      };
    }
  }, [pageState, langfuseSessionId, showFeedback]);

  if (pageState === 'completed') {
    console.log('🎬 [DEBUG] Rendering completed state:', {
      showFeedback,
      langfuseSessionId,
      willRenderDialog: !!langfuseSessionId
    });

    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          <div className="mx-4 max-w-md text-center">
            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t('guest.interviewCompleted')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {saveError ? t('guest.savedWithError') : t('guest.thankYou')}
            </p>
            {saveError && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                {saveError}
              </p>
            )}
          </div>
        </div>

        {/* Feedback Dialog */}
        {langfuseSessionId && (
          <FeedbackDialog
            isOpen={showFeedback}
            onClose={() => setShowFeedback(false)}
            sessionId={langfuseSessionId}
          />
        )}
      </>
    );
  }

  return (
    <main className="grid h-svh grid-cols-1 place-content-center">
      <AnimatePresence mode="wait">
        {!isConnectionActive && pageState === 'ready' && (
          <MotionWelcomeView
            key="welcome"
            {...VIEW_MOTION_PROPS}
            participantName={participantName}
            onStartCall={handleStartCall}
          />
        )}
        {isConnectionActive && (
          <MotionSessionView
            key="session-view"
            {...VIEW_MOTION_PROPS}
            appConfig={appConfig}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
      </AnimatePresence>
    </main>
  );
}

export function GuestInterviewPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const appConfig = getAppConfig();

  const participantId = searchParams.get('participantId');
  const designId = searchParams.get('designId');

  // Validate required params
  if (!participantId || !designId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="mx-4 max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-16 w-16 text-amber-500" />
          <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('guest.invalidLink')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('guest.invalidLinkDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Minimal Header with Logo and Language Switcher */}
      <header className="fixed top-0 left-0 z-50 flex w-full flex-row items-center justify-between p-4 md:p-6">
        <img src="/Decoa_logo_w.svg" alt="Decoa" className="h-6 md:h-7 dark:invert-0 invert" />
        <LanguageSwitcher />
      </header>

      <GuestConnectionProvider participantId={participantId} designId={designId}>
        <GuestInterviewContent
          participantId={participantId}
          designId={designId}
          appConfig={appConfig}
        />
        <StartAudio label="Start Audio" />
        <RoomAudioRenderer />
        <Toaster />
      </GuestConnectionProvider>
    </>
  );
}
